import { useCallback, useEffect, useReducer } from "react";
import { SnackbarData, SnackbarType } from "../../../components/Snackbar";
import { generateUniqueNumber } from "../../../shared/utility";
import {
  deleteProductionItem,
  fetchProductionLots,
  fetchProductionRecipe,
  fetchReceivingItem,
  saveProductionItem,
  updateProductionItem,
} from "../api/employeeProductionApi";
import {
  findRecipeForReceivingItem,
  suggestedIngredientAmount,
} from "../employeeProductionHelpers";
import {
  DropdownOption,
  EmployeeProductionDialog,
  ProductionRecipeItem,
  ReceivingItem,
  SaveScannedItemPayload,
  UpdateScannedItemPayload,
} from "../employeeProductionTypes";
import {
  employeeProductionInitialState,
  employeeProductionReducer,
} from "../employeeProductionReducer";

type ViewModelDependencies = {
  logout: () => void;
  addSnackbar: (snackbar: SnackbarData) => void;
};

function errorDialog(
  message: string,
  title = "Error",
): EmployeeProductionDialog {
  return { type: "error", title, message };
}

export function useEmployeeProductionViewModel({
  logout,
  addSnackbar,
}: ViewModelDependencies) {
  const [state, dispatch] = useReducer(
    employeeProductionReducer,
    employeeProductionInitialState,
  );

  const handleFailure = useCallback(
    (failure: { status?: number; message: string }, title = "Error") => {
      if (failure.status === 401) {
        logout();
        return;
      }
      dispatch({
        type: "dialog-opened",
        dialog: errorDialog(failure.message, title),
      });
    },
    [logout],
  );

  useEffect(() => {
    let active = true;
    void fetchProductionLots().then((result) => {
      if (!active) return;
      if (result.success) dispatch({ type: "lots-loaded", lots: result.data });
      else handleFailure(result, "Failed to fetch lot numbers");
    });
    return () => {
      active = false;
    };
  }, [handleFailure]);

  const selectProduction = useCallback(
    async (lot: DropdownOption) => {
      dispatch({ type: "production-loading", lot });
      const result = await fetchProductionRecipe(lot.value);
      if (result.success)
        dispatch({ type: "recipe-loaded", items: result.data });
      else handleFailure(result);
    },
    [handleFailure],
  );

  const scanBarcode = useCallback(
    async (rawBarcode: string) => {
      const barcode = rawBarcode.trim();
      if (!state.selectedLot || !barcode || state.status !== "idle") return;

      dispatch({ type: "barcode-loading" });
      const result = await fetchReceivingItem(barcode);
      if (!result.success) {
        handleFailure(result, "Barcode not found");
        return;
      }

      const recipe = findRecipeForReceivingItem(state.recipeItems, result.data);
      if (!recipe || recipe.amount_required == null) {
        dispatch({
          type: "dialog-opened",
          dialog: errorDialog(
            "No recipe line matches this barcode for the selected production. Pick the correct production or verify the BOM includes this ingredient.",
            "Ingredient not in this production",
          ),
        });
        return;
      }

      dispatch({
        type: "dialog-opened",
        dialog: {
          type: "confirm-scan",
          item: result.data,
          suggestedAmount: suggestedIngredientAmount(recipe, result.data),
        },
      });
    },
    [handleFailure, state.recipeItems, state.selectedLot, state.status],
  );

  const showSuccess = useCallback(
    (message: string) =>
      addSnackbar(
        new SnackbarData(generateUniqueNumber(), message, SnackbarType.Success),
      ),
    [addSnackbar],
  );

  const confirmScan = useCallback(
    async (payload: SaveScannedItemPayload) => {
      dispatch({ type: "mutation-started", status: "saving" });
      const result = await saveProductionItem(payload);
      if (result.success) {
        dispatch({
          type: "mutation-complete",
          items: result.data.recipe_items,
        });
        showSuccess(result.data.message ?? "Scanned item saved to production");
      } else handleFailure(result);
    },
    [handleFailure, showSuccess],
  );

  const confirmEdit = useCallback(
    async (payload: UpdateScannedItemPayload) => {
      dispatch({ type: "mutation-started", status: "updating" });
      const result = await updateProductionItem(payload);
      if (result.success) {
        dispatch({
          type: "mutation-complete",
          items: result.data.recipe_items,
        });
        showSuccess(result.data.message ?? "Scanned item updated");
      } else handleFailure(result);
    },
    [handleFailure, showSuccess],
  );

  const confirmDelete = useCallback(async () => {
    const item = state.dialog.type === "delete-item" ? state.dialog.item : null;
    if (!state.selectedLot || !item) return;
    dispatch({ type: "mutation-started", status: "deleting" });
    const result = await deleteProductionItem(
      state.selectedLot.value,
      item.barcode,
    );
    if (result.success) {
      dispatch({ type: "mutation-complete", items: result.data.recipe_items });
      showSuccess(
        result.data.message ?? "Scanned item deleted from production",
      );
    } else handleFailure(result);
  }, [handleFailure, showSuccess, state.dialog, state.selectedLot]);

  const openItemActions = (item: ReceivingItem) =>
    dispatch({ type: "dialog-opened", dialog: { type: "item-actions", item } });
  const openEdit = (item: ReceivingItem) =>
    dispatch({ type: "dialog-opened", dialog: { type: "edit-item", item } });
  const openDelete = (item: ReceivingItem) =>
    dispatch({ type: "dialog-opened", dialog: { type: "delete-item", item } });

  return {
    state,
    actions: {
      selectProduction,
      setBarcode: (barcode: string) =>
        dispatch({ type: "barcode-changed", barcode }),
      scanBarcode,
      confirmScan,
      confirmEdit,
      confirmDelete,
      openItemActions,
      openEdit,
      openDelete,
      closeDialog: () => dispatch({ type: "dialog-closed" }),
      setExpandedRows: (
        rows: Record<string, boolean> | ProductionRecipeItem[] | null,
      ) => dispatch({ type: "expanded-rows-changed", rows }),
    },
  };
}
